import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { formatIQD } from '../../../shared/format.js';
import { api } from '../../../shared/api.js';

// أقصر نص يُفعّل البحث — حرف واحد يعيد نصف المخزن بلا فائدة.
const MIN_TERM = 2;

/**
 * صندوق بحث منتجات بقائمة نتائج منسدلة — بالاسم أو SKU أو الباركود
 * (الخادم يبحث في الثلاثة: modules/products/services/products.js).
 *
 * يُستعمل في موضعين: رأس بطاقة الأصناف (بحث عام يضيف صنفاً جديداً) وداخل كل
 * صف من الفاتورة (يربط ذلك الصف بمنتج). التنسيق واحد في الحالتين.
 *
 * `floating` يجعل القائمة بموضع ثابت (fixed) محسوباً من موضع الحقل: داخل جدول
 * الأصناف يوجد أب بتمرير أفقي (overflow-x) يقصّ أي قائمة بموضع absolute.
 */
export function ProductSearchBox({
  value = '',
  onChange,
  onPick,
  warehouseId,
  placeholder = 'بحث بالاسم أو SKU...',
  disabled = false,
  inputClassName = 'input pr-8 text-[12.5px]',
  inputStyle,
  menuWidth = 320,
  floating = false,
  showEmpty = false,
  autoFocus = false
}) {
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [menuBox, setMenuBox] = useState(null);
  const blurTimer = useRef(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const term = String(value || '').trim();

  useEffect(() => {
    if (term.length < MIN_TERM) {
      setResults([]);
      setSearching(false);
      setActive(-1);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const params = { q: term, active: true, limit: 20 };
        if (warehouseId) params.warehouseId = warehouseId;
        const { data } = await api.get('/catalog/products', { params });
        if (cancelled) return;
        setResults(data?.items || data?.data || []);
        setActive(-1);
        setOpen(true);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [term, warehouseId]);

  // مؤقّت الإخفاء لا يجوز أن يعمل بعد إزالة المكوّن.
  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);

  const hasMenu = open && (searching || results.length > 0 || (showEmpty && term.length >= MIN_TERM));

  // قائمة ثابتة: موضعها يُحسب من الحقل، ويُعاد حسابه مع أي تمرير أو تحجيم.
  const place = useCallback(() => {
    if (!floating || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setMenuBox({ top: Math.round(r.bottom + 4), left: Math.round(r.left), minWidth: Math.round(r.width) });
  }, [floating]);

  useLayoutEffect(() => {
    if (!floating || !hasMenu) return undefined;
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [floating, hasMenu, place]);

  function close() {
    setOpen(false);
    setActive(-1);
  }

  function pick(p) {
    if (!p) return;
    close();
    onPick?.(p);
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === 'Enter' && active >= 0 && active < results.length) {
      // Enter بلا تحديد سهمي لا يختار شيئاً (سلوك البحث الأعلى كما هو).
      e.preventDefault();
      pick(results[active]);
    }
  }

  const menu = hasMenu ? (
    <div
      className="z-30 max-h-[280px] overflow-y-auto bg-white border border-line rounded-[10px] shadow-card"
      style={floating
        ? { position: 'fixed', top: menuBox?.top ?? -9999, left: menuBox?.left ?? -9999, width: menuWidth, minWidth: menuBox?.minWidth, visibility: menuBox ? 'visible' : 'hidden' }
        : { position: 'absolute', marginTop: 4, width: menuWidth, insetInlineStart: 0 }}
    >
      {searching ? (
        <div className="px-3 py-2 text-[12px] text-ink-3">جاري البحث...</div>
      ) : results.length === 0 ? (
        <div className="px-3 py-2 text-[12px] text-ink-3">لا توجد نتائج</div>
      ) : (
        results.map((p, i) => (
          <button
            key={p._id}
            type="button"
            className={`w-full text-start px-3 py-2 border-b border-line last:border-0 flex items-center justify-between gap-2 ${i === active ? 'bg-paper-2' : 'hover:bg-paper-2'}`}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setActive(i)}
            onClick={() => pick(p)}
          >
            <div className="min-w-0">
              <div className="text-[12.5px] font-semibold truncate">{p.emoji ? `${p.emoji} ` : ''}{p.name}</div>
              <div className="text-[11px] text-ink-3 truncate" dir="ltr" style={{ fontFamily: 'monospace' }}>
                {p.sku}{p.barcode ? ` • ${p.barcode}` : ''}
              </div>
            </div>
            <div className="text-[11.5px] text-ink-2 whitespace-nowrap">{formatIQD(p.costPrice || 0)}</div>
          </button>
        ))
      )}
    </div>
  ) : null;

  return (
    <div
      className="relative"
      ref={wrapRef}
      onBlur={() => { blurTimer.current = setTimeout(close, 180); }}
      onFocus={() => { if (blurTimer.current) clearTimeout(blurTimer.current); if (results.length) setOpen(true); }}
    >
      <input
        ref={inputRef}
        className={inputClassName}
        placeholder={placeholder}
        value={value}
        style={inputStyle}
        autoFocus={autoFocus}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      <Icon name="search" size={14} className="absolute top-1/2 -translate-y-1/2 right-2 text-ink-3 pointer-events-none" />
      {menu}
    </div>
  );
}
